import { addSignal } from '../addSignal';
import { intentBlockingMiddleware } from '../middleware/intentBlocking';
import { nativeNavigationHelper } from '../middleware/nativeNavigationHelper';
import { createRootReducer } from '../reducers';
import { addRunFunction } from '../runnable';
import { rootSaga } from '../sagas';
import { addSubscribeSelector } from '../subscribeSelector';
import type { SagaContext } from '../types';
import { composeWithDevTools } from '@redux-devtools/extension';
import { TypedContainer, TypedContainerModule } from '@thinkalpha/common/ioc/TypedContainer.js';
import { createBrowserHistory, createMemoryHistory } from 'history';
import type { History } from 'history';
import type { Container } from 'inversify';
import { applyMiddleware, compose, legacy_createStore as createStore } from 'redux';
import { createReduxHistoryContext } from 'redux-first-history';
import createSagaMiddleware from 'redux-saga';
import type { IsomorphicBindings } from 'src/types/bindings';

export const localStoreModule = new TypedContainerModule<IsomorphicBindings>((bind) => {
    let history: History;
    if (APP_ELECTRON) {
        history = createMemoryHistory();
    } else {
        history = createBrowserHistory();
    }

    const { routerReducer, routerMiddleware, createReduxHistory } = createReduxHistoryContext({
        history,
        showHistoryAction: true,
    });

    bind('Store')
        .toDynamicValue((ctx) => {
            const container = ctx.container;
            const context: SagaContext = {
                configService: container.get('ConfigService'),
                electronService: container.isBound('ElectronService') ? container.get('ElectronService') : undefined,
                loginScreenService: container.get('LoginScreenService'),
                oAuth2Client: container.get('OAuth2Service'),
                launchDarklyClient: container.get('LaunchDarklyClient'),
                container: new TypedContainer<IsomorphicBindings>(container as unknown as Container),
            };

            const sagaMiddleware = createSagaMiddleware({ context });

            const middlewares = [nativeNavigationHelper, intentBlockingMiddleware, routerMiddleware, sagaMiddleware];
            const middlewareEnhancer = applyMiddleware(...middlewares);

            const enhancers = compose(
                addSignal,
                addSubscribeSelector,
                addRunFunction(() => {
                    const task = sagaMiddleware.run(rootSaga);
                    return () => task.cancel();
                }),
                composeWithDevTools({ trace: TRACE, traceLimit: 10 })(middlewareEnhancer),
            );

            // Casting here since the return type of `createStore` doesn't reflect the enhancers we're adding
            const store = createStore(
                createRootReducer({ routerReducer }),
                {},
                enhancers,
            ) as IsomorphicBindings['Store'];

            // Initialize OidcLocationService with Store (native implementation requires this to break circular dependency)
            const oidcLocationService = container.get('OidcLocationService');
            oidcLocationService.initialize?.(store);

            return store;
        })
        .inSingletonScope();

    bind('History')
        .toDynamicValue((ctx) => {
            const store = ctx.container.get('Store');
            return createReduxHistory(store);
        })
        .inSingletonScope();
});
